import type { Right } from '@gozoki/tools'
import { type ButtonProps, Button } from '@mui/material'
import { type MouseEventHandler, useCallback, useState } from 'react'
import type { PermissionRequirement } from '~/utils/rbac'
import { AuthorizedButton } from './AuthorizedButton'
import { type ConfirmButtonProps, ConfirmModal } from './ConfirmModal'

interface Props {
  buttonText: string
  buttonProps: ButtonProps
  onConfirm: () => Promise<unknown>
  title: string
  description: string
  confirmButton?: ConfirmButtonProps
  cancelButton?: ConfirmButtonProps
  /** Droit(s) requis pour ouvrir la confirmation. Absent = pas de garde RBAC (legacy). */
  permission?: PermissionRequirement
  /** Droits v1 exigés tant que le flag `permission-v2` est OFF (où les tuples v2 passent tous). */
  legacyRights?: Right[]
}

export const ConfirmButton = ({
  buttonProps,
  buttonText,
  description,
  onConfirm,
  title,
  cancelButton,
  confirmButton,
  permission,
  legacyRights,
}: Props) => {
  const [isOpen, setIsOpen] = useState(false)
  const handleClose = useCallback(() => setIsOpen(false), [setIsOpen])
  const handleClick: MouseEventHandler<HTMLButtonElement> = useCallback(
    (e) => {
      setIsOpen(true)
      buttonProps.onClick?.(e)
    },
    [buttonProps],
  )

  return (
    <>
      {permission ? (
        <AuthorizedButton
          permission={permission}
          legacyRights={legacyRights}
          {...buttonProps}
          onClick={handleClick}
        >
          {buttonText}
        </AuthorizedButton>
      ) : (
        <Button {...buttonProps} onClick={handleClick}>
          {buttonText}
        </Button>
      )}
      <ConfirmModal
        open={isOpen}
        onClose={handleClose}
        onConfirm={onConfirm}
        description={description}
        title={title}
        cancelButton={cancelButton}
        confirmButton={confirmButton}
      />
    </>
  )
}
